import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { UserRole } from '@const/users.enum';
import { AuthService } from '@service/auth/auth.service';
import { UserService } from '@service/auth/user.service';
import { catchError, from, map, of, switchMap } from 'rxjs';
import { UserProfile } from '../interfaces/user.interface';

/**
 * Role-based route guard to prevent unauthorized direct URL access
 *
 * This guard checks if the user has the required role(s) to access a route.
 * If not authorized, redirects to the profile page with an error message.
 *
 * Falls back to reading roles directly from JWT token if API call fails.
 *
 * Usage in routes:
 * ```typescript
 * {
 *   path: 'carrier',
 *   canActivate: [roleGuard],
 *   data: {
 *     requiredRoles: [UserRole.ADMIN, UserRole.CARRIER],
 *     breadcrumb: 'Carrier'
 *   },
 *   loadChildren: () => import('./carrier.module').then(m => m.CarrierModule)
 * }
 * ```
 */
export const roleGuard: CanActivateFn = (route, state) => {
  const userService = inject(UserService);
  const authService = inject(AuthService);
  const router = inject(Router);

  // Get required roles from route data
  const requiredRoles = route.data['requiredRoles'] as UserRole[] | undefined;

  // If no roles specified, allow access (public route)
  if (!requiredRoles || requiredRoles.length === 0) {
    console.warn('⚠️ Route has no requiredRoles specified:', state.url);
    return true;
  }

  // Helper function to check access and navigate
  const checkAccessAndNavigate = (roles: UserRole[], email: string): boolean => {
    const hasAccess = requiredRoles.some((role) =>
      roles.map((r) => r.toLowerCase()).includes(role.toLowerCase())
    );

    if (hasAccess) {
      console.log(`✅ Access granted to ${state.url} for user ${email}`, {
        userRoles: roles,
        requiredRoles,
      });
      return true;
    }

    // Access denied - redirect to profile with error message
    console.warn(`🚫 Access denied to ${state.url} for user ${email}`, {
      userRoles: roles,
      requiredRoles,
      attemptedUrl: state.url,
    });

    // Navigate to profile page
    router.navigate(['/profile'], {
      queryParams: {
        error: 'unauthorized',
        attempted: state.url,
      },
    });

    return false;
  };

  return userService.getCurrentUser().pipe(
    map((userProfile: UserProfile) => {
      return checkAccessAndNavigate(
        (userProfile.roles || []) as UserRole[],
        userProfile.email || 'unknown'
      );
    }),
    // Fallback to reading roles directly from JWT token if API call fails
    catchError((error) => {
      console.warn('⚠️ API call failed, falling back to JWT token roles:', error);

      return from(authService.getUserRoles()).pipe(
        switchMap(async (tokenRoles) => {
          const email = await authService.getUserEmail();
          console.log('📋 Using roles from JWT token:', tokenRoles);
          return checkAccessAndNavigate(tokenRoles, email || 'unknown');
        }),
        catchError((tokenError) => {
          console.error('❌ Failed to get roles from token:', tokenError);
          // If all else fails, deny access
          router.navigate(['/profile'], {
            queryParams: {
              error: 'unauthorized',
              attempted: state.url,
            },
          });
          return of(false);
        })
      );
    })
  );
};

/**
 * Helper function to create a role guard with specific required roles
 * This is useful for creating reusable guards for specific role combinations
 */
export function createRoleGuard(requiredRoles: UserRole[]): CanActivateFn {
  return (route, state) => {
    // Inject the required roles into the route data
    route.data = { ...route.data, requiredRoles };
    return roleGuard(route, state);
  };
}

/**
 * Admin-only guard shortcut
 */
export const adminGuard: CanActivateFn = createRoleGuard([UserRole.ADMIN]);

/**
 * Admin or Tech Admin guard shortcut
 */
export const adminOrTechAdminGuard: CanActivateFn = createRoleGuard([
  UserRole.ADMIN,
  UserRole.TECH_ADMIN,
]);

/**
 * Operator guard shortcut
 */
export const operatorGuard: CanActivateFn = createRoleGuard([UserRole.ADMIN, UserRole.OPERATOR]);

/**
 * Carrier guard shortcut
 */
export const carrierGuard: CanActivateFn = createRoleGuard([UserRole.ADMIN, UserRole.CARRIER]);

/**
 * Dispatcher guard shortcut
 */
export const dispatcherGuard: CanActivateFn = createRoleGuard([
  UserRole.ADMIN,
  UserRole.DISPATCHER,
]);

/**
 * Material Admin guard shortcut
 */
export const materialAdminGuard: CanActivateFn = createRoleGuard([
  UserRole.ADMIN,
  UserRole.MATERIAL_ADMIN,
]);

/**
 * Operational Admin guard shortcut
 */
export const operationalAdminGuard: CanActivateFn = createRoleGuard([
  UserRole.ADMIN,
  UserRole.OPERATIONAL_ADMIN,
]);
